import { Information, InformationFilled, WarningFilled } from '@carbon/icons-react';
import { useTheme } from '@repo/theme';
import { UtilityText } from 'component/text';
import { useTranslation } from 'component/translation';
import { BasePopover } from 'component/ui/base-popover';
import { useState } from 'react';
import { Button, DialogTrigger } from 'react-aria-components';
import { type TLabel, t } from 'util/stdtypes';

import styles from './popover-tip.module.css';

export type PopoverTipProps = {
    label?: TLabel;
    text?: string;
    ariaLabel?: TLabel;
    popupGap?: number;
    type?: 'default' | 'warning';
};

const defaultAriaLabel = t('popup_tip.more_information', 'More information');

/**
 * Information-icon trigger that opens a short explanatory popover when pressed.
 * `PopoverTip` is a "popup tip" in
 * [NN/g's tooltip taxonomy](https://www.nngroup.com/articles/tooltip-guidelines/):
 * click-revealed (so it works across pointer, keyboard, and touch), explicitly
 * dismissed by the user, and used for *supplementary* context — clarifying
 * unfamiliar terminology, naming why a field exists, or giving a one-paragraph
 * rationale that would otherwise crowd the inline UI. Don't put information
 * the user needs to *act on* in here (validation rules, blocking error
 * explanations, instructions required to complete the task) — render that
 * inline so users don't have to hunt for it.
 */
export function PopoverTip({ label, text, ariaLabel, popupGap, type = 'default' }: PopoverTipProps) {
    const { colors } = useTheme();
    const [hover, setHover] = useState(false);
    const [open, setOpen] = useState(false);
    const translatedAriaLabel = useTranslation(ariaLabel ?? defaultAriaLabel);

    const popup = (
        <DialogTrigger onOpenChange={setOpen}>
            <Button aria-label={translatedAriaLabel} className={styles.button} data-testid='popover-tip' onHoverChange={setHover}>
                {type === 'warning' ? (
                    <WarningFilled color={colors.colorTextWarning} />
                ) : hover || open ? (
                    <InformationFilled color={colors.colorTextPrimary} />
                ) : (
                    <Information color={colors.colorTextSecondary} />
                )}
            </Button>

            <BasePopover arrowClassName={styles.arrow} dark offset={popupGap} popoverClassName={styles.popup} spacing='tight'>
                <div className={styles.content}>
                    <UtilityText color={colors.colorTextOnToastDark} label={label} text={text} />
                </div>
            </BasePopover>
        </DialogTrigger>
    );

    return popup;
}
